import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabase';
import { isRenderMode } from './renderMode';
import Auth from './Auth';
import { DataProvider } from './context/DataContext';
import { RoleProvider, useRole } from './context/RoleContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import { B } from './utils/constants';

// Lazy-loaded page components — each becomes its own chunk
const Home = lazy(() => import('./pages/Home'));
const SupplyRoundUp = lazy(() => import('./pages/SupplyRoundUpNew'));
const VendorScorecard = lazy(() => import('./pages/VendorScorecard'));
const VendorLineUp = lazy(() => import('./pages/VendorLineUp'));
const VendorApprovals = lazy(() => import('./pages/VendorApprovals'));
const VendorTanneries = lazy(() => import('./pages/VendorTanneries'));
const QBR = lazy(() => import('./pages/QBR'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const Import = lazy(() => import('./pages/Import'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const WildWest = lazy(() => import('./pages/WildWest'));
const Backlog = lazy(() => import('./pages/Backlog'));
const Claude = lazy(() => import('./pages/Claude'));
const TrailNotes = lazy(() => import('./pages/TrailNotes'));
const LaunchLineUp = lazy(() => import('./pages/LaunchLineUp'));
const MexicoProductionTracker = lazy(() => import('./pages/MexicoProductionTracker'));
const SupplyPlan = lazy(() => import('./pages/SupplyPlan'));
const BuyReady = lazy(() => import('./pages/BuyReady'));
const CustomBoots = lazy(() => import('./pages/CustomBoots'));
const EmbroideryOrders = lazy(() => import('./pages/EmbroideryOrders'));
const GritAndGrind = lazy(() => import('./pages/GritAndGrind'));
const Changelog = lazy(() => import('./pages/Changelog'));
const TheBarn = lazy(() => import('./pages/TheBarn'));
const CargoSignal = lazy(() => import('./pages/CargoSignal'));
const TheExchange = lazy(() => import('./pages/TheExchange'));
const QualityConcerns = lazy(() => import('./pages/QualityConcerns'));
const Outpost = lazy(() => import('./pages/Outpost'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const CarrierConfig = lazy(() => import('./pages/CarrierConfig'));
const FeatureRequest = lazy(() => import('./pages/FeatureRequest'));
const FeatureRequestsAdmin = lazy(() => import('./pages/FeatureRequestsAdmin'));
const BootsOnTheMove = lazy(() => import('./pages/BootsOnTheMove'));
const OrderRecon = lazy(() => import('./pages/OrderRecon'));
const StoreOrderSchedule = lazy(() => import('./pages/StoreOrderSchedule'));
const POPriceAudit = lazy(() => import('./pages/POPriceAudit'));
const CashFlow = lazy(() => import('./pages/CashFlow'));
const ProcessMap = lazy(() => import('./pages/ProcessMap'));
const PLMDashboards = lazy(() => import('./pages/PLMDashboards'));
const SeasonOverSeason = lazy(() => import('./pages/SeasonOverSeason'));
const MaterialPlanning = lazy(() => import('./pages/MaterialPlanning'));
const ApparelMaterialPlanning = lazy(() => import('./pages/ApparelMaterialPlanning'));
const AccessoryMaterialPlanning = lazy(() => import('./pages/AccessoryMaterialPlanning'));
const MarginBuilder = lazy(() => import('./pages/MarginBuilder'));
const MarginBuilderAssumptions = lazy(() => import('./pages/MarginBuilderAssumptions'));
const CIBriefing = lazy(() => import('./pages/CIBriefing'));
const SupplyChainBriefing = lazy(() => import('./pages/SupplyChainBriefing'));
const WholesaleSLA = lazy(() => import('./pages/WholesaleSLA'));
const GeodisClaim = lazy(() => import('./pages/GeodisClaim'));
const VendorActions = lazy(() => import('./pages/VendorActions'));
const CustomerJourneyOperator = lazy(() => import('./pages/CustomerJourneyOperator'));
const CustomerJourney = lazy(() => import('./pages/CustomerJourney'));
const WholesaleLanding = lazy(() => import('./pages/wholesale/WholesaleLanding'));
const ZM10Report = lazy(() => import('./pages/wholesale/ZM10Report'));
const TrailCount = lazy(() => import('./pages/theBarn/TrailCount'));
const StoreInbound = lazy(() => import('./pages/StoreInbound'));
const WholesaleFillRate = lazy(() => import('./pages/wholesale/WholesaleFillRate'));
const WholesalePriceCheck = lazy(() => import('./pages/wholesale/WholesalePriceCheck'));
const WFXProjectionImport = lazy(() => import('./pages/WFXProjectionImport'));
const ProjectionVsPO = lazy(() => import('./pages/ProjectionVsPO'));

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin" style={{ borderColor: B.forest, borderTopColor: 'transparent' }} />
  </div>
);

const isPasswordRecoveryFlow = () => {
  const hash = window.location.hash;
  const pathname = window.location.pathname;
  if (hash.includes('type=recovery')) return true;
  if (pathname.includes('reset-password')) return true;
  return false;
};

const ShortcutsModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" style={{ border: `2px solid ${B.sand}` }} onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold" style={{ color: B.forest }}>Keyboard Shortcuts</h2>
        <button onClick={onClose} className="text-2xl" style={{ color: B.stone }}>&times;</button>
      </div>
      <div className="space-y-3">
        <div className="pb-3" style={{ borderBottom: `1px solid ${B.sand}` }}>
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: B.olive }}>Navigation</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span style={{ color: B.stone }}>Go to Home</span><span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>G</kbd> then <kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>H</kbd></span></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Go to Supply Round Up</span><span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>G</kbd> then <kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>S</kbd></span></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Go to Vendor Line Up</span><span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>G</kbd> then <kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>V</kbd></span></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Go to QBR</span><span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>G</kbd> then <kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>Q</kbd></span></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Go to Pipeline</span><span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>G</kbd> then <kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>P</kbd></span></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Go to Watchtower</span><span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>G</kbd> then <kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>W</kbd></span></div>
          </div>
        </div>
        <div className="pb-3" style={{ borderBottom: `1px solid ${B.sand}` }}>
          <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: B.olive }}>Actions</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span style={{ color: B.stone }}>Focus Search</span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>/</kbd></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Show Shortcuts</span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>?</kbd></div>
            <div className="flex justify-between"><span style={{ color: B.stone }}>Close Modal / Cancel</span><kbd className="px-2 py-1 rounded text-xs" style={{ backgroundColor: B.cream }}>Esc</kbd></div>
          </div>
        </div>
      </div>
      <p className="text-xs mt-4 text-center" style={{ color: B.stone }}>Press <kbd className="px-1.5 py-0.5 rounded" style={{ backgroundColor: B.cream }}>?</kbd> anytime to see this menu</p>
    </div>
  </div>
);

// Main app content with routing
function AppContent({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);
  const { hasAccess, loading: roleLoading, defaultLandingPath } = useRole();

  // One-time default-landing redirect: if the user has a configured default
  // landing page and arrives at the root, send them there once. After that they
  // can freely navigate to Home — we never bounce them again this session.
  const didDefaultRedirect = useRef(false);

  // Record visited pages for the Homestead's "recent trails" chips.
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return;
    const name = path.split('/').filter(Boolean).pop()
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    try {
      const prev = JSON.parse(localStorage.getItem('homestead_recent_pages') || '[]')
        .filter((r) => r.path !== path);
      localStorage.setItem('homestead_recent_pages', JSON.stringify([{ path, name }, ...prev].slice(0, 6)));
    } catch { /* ignore */ }
  }, [location.pathname]);

  useEffect(() => {
    if (didDefaultRedirect.current || roleLoading) return;
    if (location.pathname !== '/') { didDefaultRedirect.current = true; return; }
    if (defaultLandingPath && defaultLandingPath !== '/') {
      didDefaultRedirect.current = true;
      navigate(defaultLandingPath, { replace: true });
    }
  }, [roleLoading, defaultLandingPath, location.pathname, navigate]);

  // Get active page from URL path
  const getActivePageFromPath = (pathname) => {
    if (pathname === '/' || pathname === '/home') return 'home';
    if (pathname.startsWith('/supply-chain/round-up')) return 'supply-roundup';
    if (pathname.startsWith('/vendors/approvals')) return 'vendor-approvals';
    if (pathname.startsWith('/vendors/line-up')) return 'vendors-lineup';
    if (pathname.startsWith('/vendors/scorecard')) return 'vendors-scorecard';
    if (pathname.startsWith('/vendors/tanneries')) return 'vendors-tanneries';
    if (pathname.startsWith('/supply-chain/qbr')) return 'qbr';
    if (pathname.startsWith('/supply-chain/pipeline')) return 'pipeline';
    if (pathname.startsWith('/wholesale')) return 'wholesale';
    if (pathname.startsWith('/data-corral')) return 'import';
    if (pathname.startsWith('/wild-west')) return 'wild-west';
    if (pathname.startsWith('/user-guide')) return 'user-guide';
    return 'home';
  };

  const activePage = getActivePageFromPath(location.pathname);

  // Navigation handler that updates URL
  const handleNavigate = useCallback((pageId) => {
    const routes = {
      'home': '/',
      'supply-roundup': '/supply-chain/round-up',
      'vendor-approvals': '/vendors/approvals',
      'vendors-lineup': '/vendors/line-up',
      'vendors-scorecard': '/vendors/scorecard',
      'vendors-tanneries': '/vendors/tanneries',
      'qbr': '/supply-chain/qbr',
      'pipeline': '/supply-chain/pipeline',
      'the-barn': '/supply-chain/the-barn',
      'store-inbound': '/supply-chain/store-inbound',
      'the-exchange': '/supply-chain/the-exchange',
      'quality-concerns': '/supply-chain/quality-concerns',
      'import': '/data-corral',
      'wild-west': '/wild-west',
      'user-guide': '/user-guide',
      'feature-request': '/feature-request',
      'backlog': '/backlog'
    };
    navigate(routes[pageId] || '/');
  }, [navigate]);

  const handleKeyDown = useCallback((e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
    if (e.key === '?') { e.preventDefault(); setShowShortcuts(true); return; }
    if (e.key === 'Escape') { setShowShortcuts(false); setPendingKey(null); return; }
    if (e.key === '/' && !pendingKey) { e.preventDefault(); const s = document.querySelector('input[placeholder*="Search"]'); if (s) s.focus(); return; }
    if (e.key.toLowerCase() === 'g' && !pendingKey) { setPendingKey('g'); setTimeout(() => setPendingKey(null), 1500); return; }
    if (pendingKey === 'g') {
      const routes = {
        'h': { page: 'home', path: '/' },
        's': { page: 'supply-roundup', path: '/supply-chain/round-up' },
        'v': { page: 'vendors-lineup', path: '/vendors/line-up' },
        'q': { page: 'qbr', path: '/supply-chain/qbr' },
        'p': { page: 'pipeline', path: '/supply-chain/pipeline' },
        'w': { page: 'supply-roundup', path: '/supply-chain/round-up/watchtower' }
      };
      const routeInfo = routes[e.key.toLowerCase()];
      if (routeInfo && hasAccess('page', routeInfo.page)) {
        e.preventDefault();
        navigate(routeInfo.path);
      }
      setPendingKey(null);
    }
  }, [pendingKey, handleNavigate, navigate]);

  useEffect(() => { 
    window.addEventListener('keydown', handleKeyDown); 
    return () => window.removeEventListener('keydown', handleKeyDown); 
  }, [handleKeyDown]);

  return (
    <>
      <Layout activePage={activePage} onNavigate={handleNavigate} user={user} onLogout={onLogout}>
        <Suspense fallback={<PageLoader />}>
        <div className="animate-fadeIn">
          <Routes>
            {/* Home - Always accessible */}
            <Route path="/" element={<Home onNavigate={handleNavigate} />} />
            <Route path="/home" element={<Navigate to="/" replace />} />

            {/* Supply Chain Routes - Protected */}
            <Route path="/supply-chain" element={<Navigate to="/supply-chain/supply-plan" replace />} />
            <Route path="/supply-chain/round-up" element={<Navigate to="/supply-chain/supply-plan" replace />} />
            <Route path="/supply-chain/round-up/:section" element={<Navigate to="/supply-chain/supply-plan" replace />} />
            <Route path="/supply-chain/round-up/po/:poNumber" element={<Navigate to="/supply-chain/grit-grind" replace />} />
            <Route path="/supply-chain/grit-grind" element={<ProtectedRoute resourceKey="grit-grind"><GritAndGrind /></ProtectedRoute>} />
            <Route path="/vendors/line-up" element={<ProtectedRoute resourceKey="vendors-lineup"><VendorLineUp /></ProtectedRoute>} />
            <Route path="/vendors/scorecard" element={<ProtectedRoute resourceKey="vendors-scorecard"><VendorScorecard /></ProtectedRoute>} />
            <Route path="/vendors/tanneries" element={<ProtectedRoute resourceKey="vendors-tanneries"><VendorTanneries /></ProtectedRoute>} />
            <Route path="/vendors/vendor-actions" element={<ProtectedRoute resourceKey="vendor-actions"><VendorActions /></ProtectedRoute>} />
            <Route path="/vendors/approvals" element={<ProtectedRoute resourceKey="vendor-approvals"><VendorApprovals /></ProtectedRoute>} />
            <Route path="/supply-chain/qbr" element={<ProtectedRoute resourceKey="qbr"><QBR /></ProtectedRoute>} />
            <Route path="/supply-chain/qbr/q1-2026" element={<ProtectedRoute resourceKey="qbr"><QBR /></ProtectedRoute>} />
            <Route path="/supply-chain/qbr/wholesale" element={<ProtectedRoute resourceKey="qbr-wholesale"><WholesaleSLA /></ProtectedRoute>} />
            <Route path="/supply-chain/pipeline" element={<ProtectedRoute resourceKey="pipeline"><Pipeline /></ProtectedRoute>} />
            <Route path="/supply-chain/cargo-signal" element={<ProtectedRoute resourceKey="cargo-signal"><CargoSignal /></ProtectedRoute>} />
            <Route path="/supply-chain/the-barn" element={<ProtectedRoute resourceKey="the-barn"><TheBarn /></ProtectedRoute>} />
            <Route path="/supply-chain/trail-count" element={<ProtectedRoute resourceKey="trail-count"><TrailCount /></ProtectedRoute>} />
            <Route path="/supply-chain/store-inbound" element={<ProtectedRoute resourceKey="store-inbound"><StoreInbound /></ProtectedRoute>} />
            <Route path="/supply-chain/the-exchange" element={<ProtectedRoute resourceKey="the-exchange"><TheExchange /></ProtectedRoute>} />
            <Route path="/supply-chain/quality-concerns" element={<ProtectedRoute resourceKey="quality-concerns"><QualityConcerns /></ProtectedRoute>} />
                        <Route path="/supply-chain/trail-notes" element={<ProtectedRoute resourceKey="trail-notes"><TrailNotes /></ProtectedRoute>} />
            <Route path="/supply-chain/launch-lineup" element={<ProtectedRoute resourceKey="launch-lineup"><LaunchLineUp /></ProtectedRoute>} />
            <Route path="/supply-chain/mx-launch-tracker" element={<ProtectedRoute resourceKey="mx-launch-tracker"><MexicoProductionTracker /></ProtectedRoute>} />
            <Route path="/supply-chain/supply-plan" element={<ProtectedRoute resourceKey="supply-plan"><SupplyPlan /></ProtectedRoute>} />
            {/* Projection vs POs — Jay Rupani's plan-vs-placed report. Lives
                under Supply Plan (Alexis 2026-09-01): same open/received PO
                units, laid against the WFX receipt plan. */}
            <Route path="/supply-chain/projection-vs-po" element={<ProtectedRoute resourceKey="projection-vs-po"><ProjectionVsPO /></ProtectedRoute>} />
            <Route path="/supply-chain/buy-ready" element={<ProtectedRoute resourceKey="buy-ready"><BuyReady /></ProtectedRoute>} />
            <Route path="/supply-chain/custom-boots" element={<ProtectedRoute resourceKey="custom-boots"><CustomBoots /></ProtectedRoute>} />
            <Route path="/supply-chain/embroidery" element={<ProtectedRoute resourceKey="embroidery"><EmbroideryOrders /></ProtectedRoute>} />

            {/* Admin Routes - Protected */}
            <Route path="/data-corral" element={<ProtectedRoute resourceKey="data-corral"><Import /></ProtectedRoute>} />
            <Route path="/wild-west" element={<ProtectedRoute resourceKey="wild-west"><WildWest /></ProtectedRoute>} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route path="/feature-request" element={<FeatureRequest user={user} />} />
            <Route path="/backlog" element={<ProtectedRoute resourceKey="backlog"><Backlog /></ProtectedRoute>} />
            <Route path="/changelog" element={<ProtectedRoute resourceKey="changelog"><Changelog /></ProtectedRoute>} />
            <Route path="/outpost" element={<ProtectedRoute resourceKey="outpost"><Outpost /></ProtectedRoute>} />
            <Route path="/process-map" element={<ProtectedRoute resourceKey="process-map"><ProcessMap /></ProtectedRoute>} />
            <Route path="/claude" element={<Claude />} />

            {/* Role Management Routes - Admin Only */}
            <Route path="/admin/roles" element={<ProtectedRoute resourceKey="admin-roles"><RoleManagement /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute resourceKey="admin-users"><UserManagement /></ProtectedRoute>} />
            <Route path="/admin/carriers" element={<ProtectedRoute resourceKey="admin-carriers"><CarrierConfig /></ProtectedRoute>} />
            <Route path="/admin/feature-requests" element={<ProtectedRoute resourceKey="admin-feature-requests"><FeatureRequestsAdmin /></ProtectedRoute>} />

            {/* Customer Routes */}
            <Route path="/customer/journey" element={<ProtectedRoute resourceKey="customer-journey"><CustomerJourney /></ProtectedRoute>} />
            <Route path="/customer/boots-on-the-move" element={<ProtectedRoute resourceKey="boots-on-the-move"><BootsOnTheMove /></ProtectedRoute>} />
            <Route path="/customer/order-recon" element={<ProtectedRoute resourceKey="order-recon"><OrderRecon /></ProtectedRoute>} />
            <Route path="/customer/store-order-schedule" element={<ProtectedRoute resourceKey="store-order-schedule"><StoreOrderSchedule /></ProtectedRoute>} />
            <Route path="/supply-chain/po-price-audit" element={<ProtectedRoute resourceKey="po-price-audit"><POPriceAudit /></ProtectedRoute>} />
            <Route path="/supply-chain/cash-flow" element={<ProtectedRoute resourceKey="cash-flow"><CashFlow /></ProtectedRoute>} />
            <Route path="/customer-journey/operations" element={<ProtectedRoute resourceKey="customer-journey-operations"><CustomerJourneyOperator /></ProtectedRoute>} />

            <Route path="/ci-briefing" element={<ProtectedRoute resourceKey="ci-briefing"><CIBriefing /></ProtectedRoute>} />
            <Route path="/geodis-claim" element={<GeodisClaim />} />
            <Route path="/supply-chain-briefing" element={<SupplyChainBriefing />} />

            {/* The Workshop Routes (WFX-sourced product & merch data).
                Renamed from "/mercantile" to match the v1.71.0 nav relabel
                (The Mercantile → The Workshop, PLM Dashboards → Margin Reporting). */}
            <Route path="/workshop" element={<Navigate to="/workshop/margin-reporting/season-reporting" replace />} />
            {/* Margin Reporting – Season Reporting (single-range planning view) */}
            <Route path="/workshop/margin-reporting" element={<Navigate to="/workshop/margin-reporting/season-reporting" replace />} />
            <Route path="/workshop/margin-reporting/season-reporting" element={<ProtectedRoute resourceKey="plm-dashboards"><PLMDashboards /></ProtectedRoute>} />
            {/* Margin Reporting – Season over Season (multi-range comparison) */}
            <Route path="/workshop/margin-reporting/season-over-season" element={<ProtectedRoute resourceKey="plm-dashboards"><SeasonOverSeason /></ProtectedRoute>} />
            {/* Material Planning – Leather (footwear) keeps the original URL so
                existing bookmarks and links are untouched; Apparel is a sibling
                page behind the same resource key. */}
            <Route path="/workshop/material-planning" element={<ProtectedRoute resourceKey="material-planning"><MaterialPlanning /></ProtectedRoute>} />
            {/* Nav label and page title read "Apparel Fabric Planning"; the URL, resource key and component name stay as-is. */}
            <Route path="/workshop/material-planning/apparel" element={<ProtectedRoute resourceKey="material-planning"><ApparelMaterialPlanning /></ProtectedRoute>} />
            <Route path="/workshop/material-planning/accessories" element={<ProtectedRoute resourceKey="material-planning"><AccessoryMaterialPlanning /></ProtectedRoute>} />
            {/* Margin Builder — PD's pre-costing estimator (seed from WFX, swap components, override anything).
                Assumptions is the PD-managed defaults editor behind its own resource key (grant Taylor + admins). */}
            <Route path="/workshop/margin-builder" element={<ProtectedRoute resourceKey="margin-builder"><MarginBuilder /></ProtectedRoute>} />
            <Route path="/workshop/margin-builder/assumptions" element={<ProtectedRoute resourceKey="margin-builder-assumptions"><MarginBuilderAssumptions /></ProtectedRoute>} />

            {/* Planning — WFX Projection Import. Replaces the planning team's
                Apps Script "WFX Sync": paste the buy plan, match it to WFX
                colorways, push unit projections back through the
                wfx-projection-push edge function. Sits under /planning even
                though its nav home is The Workshop — it's Planning's page. */}
            <Route path="/planning" element={<Navigate to="/planning/wfx-import" replace />} />
            <Route path="/planning/wfx-import" element={<ProtectedRoute resourceKey="wfx-projection-import"><WFXProjectionImport /></ProtectedRoute>} />

            {/* Legacy "/mercantile" redirects — kept so old bookmarks, QBR
                snapshots, and shared links still resolve after the rename. */}
            <Route path="/mercantile" element={<Navigate to="/workshop/margin-reporting/season-reporting" replace />} />
            <Route path="/mercantile/plm-dashboards" element={<Navigate to="/workshop/margin-reporting/season-reporting" replace />} />
            <Route path="/mercantile/plm-dashboards/season-reporting" element={<Navigate to="/workshop/margin-reporting/season-reporting" replace />} />
            <Route path="/mercantile/plm-dashboards/season-over-season" element={<Navigate to="/workshop/margin-reporting/season-over-season" replace />} />
            <Route path="/mercantile/material-planning" element={<Navigate to="/workshop/material-planning" replace />} />
            <Route path="/mercantile/merch-dashboards" element={<Navigate to="/workshop/margin-reporting/season-reporting" replace />} />

            {/* Legacy redirects */}
            <Route path="/supply-roundup" element={<Navigate to="/supply-chain/round-up" replace />} />
            <Route path="/vendor-scorecard" element={<Navigate to="/vendors/scorecard" replace />} />
            <Route path="/supply-chain/vendor-scorecard" element={<Navigate to="/vendors/scorecard" replace />} />
            <Route path="/qbr" element={<Navigate to="/supply-chain/qbr" replace />} />
            <Route path="/pipeline" element={<Navigate to="/supply-chain/pipeline" replace />} />
            <Route path="/import" element={<Navigate to="/data-corral" replace />} />

            {/* Wholesale microsite */}
            <Route path="/wholesale" element={<ProtectedRoute resourceKey="wholesale"><WholesaleLanding /></ProtectedRoute>} />
            <Route path="/wholesale/zm10" element={<ProtectedRoute resourceKey="wholesale-zm10"><ZM10Report /></ProtectedRoute>} />
            <Route path="/wholesale/fill-rate" element={<ProtectedRoute resourceKey="wholesale-fill-rate"><WholesaleFillRate /></ProtectedRoute>} />
            <Route path="/wholesale/price-check" element={<ProtectedRoute resourceKey="wholesale-price-check"><WholesalePriceCheck /></ProtectedRoute>} />

            {/* Catch all - Redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </Suspense>
      </Layout>
      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
      {pendingKey && (
        <div className="fixed bottom-4 right-4 px-3 py-2 rounded-lg shadow-lg" style={{ backgroundColor: B.forest, color: '#fff' }}>
          <span className="text-sm">Press: </span>
          <kbd className="px-2 py-1 rounded ml-1" style={{ backgroundColor: B.olive }}>H</kbd>
          <kbd className="px-2 py-1 rounded ml-1" style={{ backgroundColor: B.olive }}>S</kbd>
          <kbd className="px-2 py-1 rounded ml-1" style={{ backgroundColor: B.olive }}>V</kbd>
          <kbd className="px-2 py-1 rounded ml-1" style={{ backgroundColor: B.olive }}>Q</kbd>
          <kbd className="px-2 py-1 rounded ml-1" style={{ backgroundColor: B.olive }}>P</kbd>
          <kbd className="px-2 py-1 rounded ml-1" style={{ backgroundColor: B.olive }}>W</kbd>
        </div>
      )}
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  useEffect(() => {
    if (isPasswordRecoveryFlow()) { setIsRecoveryMode(true); setAuthLoading(false); return; }
    supabase.auth.getSession().then(({ data: { session } }) => { setUser(session?.user ?? null); setAuthLoading(false); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') { setIsRecoveryMode(true); return; }
      if (event === 'USER_UPDATED' && isRecoveryMode) { setIsRecoveryMode(false); setUser(null); window.history.replaceState({}, document.title, '/'); return; }
      if (!isRecoveryMode) setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, [isRecoveryMode]);

  const handleLogout = async () => { await supabase.auth.signOut(); setUser(null); };
  const handleLogin = (loggedInUser) => { setIsRecoveryMode(false); window.history.replaceState({}, document.title, '/'); setUser(loggedInUser); };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: B.cream }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: B.forest, borderTopColor: 'transparent' }} />
          <p style={{ color: B.forest, fontFamily: 'Gill Sans, sans-serif' }}>Saddling up...</p>
        </div>
      </div>
    );
  }


  // Export pipeline: when the dataset is injected, render ONLY the Round Up Report
  // from it — no login, no Layout/sidebar/nav, no live API calls. The GitHub Action
  // loads this, then screenshots the report. Filters come from the URL query string.
  if (isRenderMode) {
    return (
      <BrowserRouter>
        <DataProvider>
          <RoleProvider user={{ id: 'render-export' }}>
            <Suspense fallback={<div />}>
              <div id="render-export-root" style={{ background: '#fff' }}>
                {window.__RENDER_DATA__?.report === 'mx' ? <MexicoProductionTracker /> : <LaunchLineUp />}
              </div>
            </Suspense>
          </RoleProvider>
        </DataProvider>
      </BrowserRouter>
    );
  }

  if (!user || isRecoveryMode) return <Auth onLogin={handleLogin} />;

  return (
    <BrowserRouter>
      <DataProvider>
        <RoleProvider user={user}>
          <AppContent user={user} onLogout={handleLogout} />
        </RoleProvider>
      </DataProvider>
    </BrowserRouter>
  );
}
